import { TLocale } from '@internal/utils/machine/i18n/TLocale'
import { SystemPageKey } from 'page/SystemPageKey'
import { ParsedUrlQuery } from 'querystring'
import slugify from 'slugify'
import { PageTypeKey } from 'utils/page/PageTypeKey'
import {
  routingTable,
  TCompareMachinesRouteParams,
  TDashboardRouteParams,
  TMachineCategoryPageRouteParams,
  TMachineDetailsRouteParams,
  TSearchResultsRouteParams,
} from '../routingTable'
import { getSlugifyOptions } from '../slugs/getSlugifyOptions'
import { TRoute } from '../TRoute'
import { queryParamsToString } from './queryParamsToString'
import { removeDoubleSlashes } from './removeDoubleSlashes'

type TParams =
  | {
      key: SystemPageKey.StartPage
    }
  | ({
      key: SystemPageKey.Dashboard
    } & (
      | {
          userId?: string
          machineGroupId?: string
          inspection?: boolean
          dashboardRoute?: undefined
        }
      | {
          userId?: undefined
          machineGroupId?: undefined
          inspection?: undefined
          dashboardRoute: string
        }
    ))
  | {
      key: SystemPageKey.SearchResults
      machineTypeName?: string
      queryParams?: TSearchResultsRouteParams['queryParams']
    }
  | ({
      key: SystemPageKey.PDP
    } & TMachineDetailsRouteParams)
  | {
      key: 'slug'
      slug: string
      queryParams?: ParsedUrlQuery
    }
  | ({
      key: SystemPageKey.CompareMachines
    } & TCompareMachinesRouteParams)
  | {
      key: PageTypeKey.MachineCategoryPage
      machineTypeName: string
      brandSlug?: string | null
      modelSlug?: string | null
      queryParams?: ParsedUrlQuery
    }

export const urlFor = (locale: TLocale, params: TParams | string) => {
  if (typeof params === 'string') {
    const slug = params
    return removeDoubleSlashes(`/${locale}/${slug}/`)
  } else if (params.key === 'slug') {
    const url = removeDoubleSlashes(`/${locale}/${params.slug}/`)
    const queryString =
      params.queryParams && queryParamsToString(params.queryParams)
    return `${url}${queryString && `?${queryString}`}`
  } else if (params.key === SystemPageKey.SearchResults) {
    const route = routingTable.systemPageRoutes[SystemPageKey.SearchResults][
      locale
    ] as TRoute<TSearchResultsRouteParams>

    const machineTypeSlug = params.machineTypeName
      ? slugify(params.machineTypeName, getSlugifyOptions(locale))
      : undefined
    return route.compile({
      pathParams: {
        machineTypeSlug: machineTypeSlug,
      },
      queryParams: params.queryParams ?? {},
    })
  } else if (params.key === PageTypeKey.MachineCategoryPage) {
    const route = routingTable.machineCategoryPageRoutes[
      locale
    ] as TRoute<TMachineCategoryPageRouteParams>

    const machineTypeSlug = slugify(
      params.machineTypeName,
      getSlugifyOptions(locale)
    )
    return route.compile({
      pathParams: {
        machineTypeSlug: machineTypeSlug,
        brandSlug: params.brandSlug ?? undefined,
        modelSlug: params.modelSlug ?? undefined,
      },
      queryParams: params.queryParams ?? {},
    })
  } else if (params.key === SystemPageKey.Dashboard) {
    const route = routingTable.systemPageRoutes[SystemPageKey.Dashboard][
      locale
    ] as TRoute<TDashboardRouteParams>

    const dashboardUrl = route.compile({
      pathParams: {},
      queryParams: {},
    })

    const elements: string[] = []
    if (params.userId) {
      elements.push(params.userId)
    }

    if (params.machineGroupId) {
      elements.push(`machine/${params.machineGroupId.toLowerCase()}`)
    }

    if (params.inspection) {
      elements.push('inspection')
    }
    const dashboardRoute = params.dashboardRoute
      ? `#${params.dashboardRoute}`
      : elements.length > 0
        ? `#/${elements.join('/')}/`
        : ''
    return `${dashboardUrl}${dashboardRoute}`
  }

  return routingTable.systemPageRoutes[params.key][locale].compile(params)
}
